import {Button} from '@/components/ui/button/Button'
import {DialogDisclosure, DialogProvider} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {AlertCircleIcon} from '@/components/ui/icon/Icon'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Text} from '@/components/ui/text/Text'
import {AgendaEventDetailsDialog} from '@/features/agenda/components/agenda-event-details-dialog/AgendaEventDetailsDialog'
import {useGetEvent} from '@/features/agenda/queries/useGetEvent'
import {StyledProjectWarningMissingBanner} from '@/features/projects/components/project-warning-missing-banner/style'
import {Trans, useTranslation} from '@/translations/i18n'

/**
 * Warning shown when a renewed project has a renewal event that is present but not yet
 * completed (isRenewedProjectRenewalEventCompleted === false).
 *
 * The warning text is flag-driven and always renders; the "Vedi evento" action is
 * best-effort and only appears once the renewal event has been resolved from the
 * project's events (eventId provided by the parent banner).
 */
export const ProjectWarningMissingRenewalEventCompleted = ({eventId}: {eventId?: string}) => {
    const {t} = useTranslation()

    return (
        <StyledProjectWarningMissingBanner align="center">
            <Flexbox gap={1} align="center">
                <AlertCircleIcon />
                <Text palette="neutral" shade="950" size="textSm">
                    <Trans
                        i18nKey="projects:missing_event_content_completed"
                        components={{b: <b />}}
                        values={{
                            event: t('agenda:access_project_renewal_processing')
                        }}
                    />
                </Text>
            </Flexbox>
            {eventId && <ProjectWarningRenewalEventButton eventId={eventId} />}
        </StyledProjectWarningMissingBanner>
    )
}

const ProjectWarningRenewalEventButton = ({eventId}: {eventId: string}) => {
    const {t} = useTranslation()
    const {data: event, isPending} = useGetEvent({
        eventId,
        responseType: 'extended'
    })

    if (isPending)
        return (
            <Button size="sm" variant="tertiary" disabled>
                <Spinner />
            </Button>
        )

    if (!event) return null

    return (
        <DialogProvider>
            <DialogDisclosure
                render={
                    <Button size="sm" variant="tertiary">
                        {t('projects:view_event')}
                    </Button>
                }
            />
            <AgendaEventDetailsDialog eventId={event.id} />
        </DialogProvider>
    )
}
